import React from 'react';
import { Flex } from '@adobe/react-spectrum';
import './FAQ.css';
import { Helmet } from 'react-helmet';
import { FaqPane } from './FaqPane';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { getDomainPrefix } from '../../data/DynamoConnector';
import { NitroAd } from '../../ads/NitroAd';

export function FAQ() {
    return (
        <Flex direction="column">
            <Helmet>
                <title>FAQ</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href="https://www.commandersalt.com/faq" />
                <meta name="description" content="Frequently asked questions" />
                <meta name="twitter:title" content="Commandersalt.com - FAQ" />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content="Commandersalt.com - FAQ" />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content="Frequently asked questions" />
            </Helmet>
            <BreadCrumbs />
            <div className="faqPageWrap">
                <section className="faqHero">
                    <span className="faqHeroEyebrow">FAQ · Hello!</span>
                    <h1 className="faqHeroTitle">Salt, Answered.</h1>
                    <div className="faqHeroLead">
                        <p>
                            This site is a hobby project funded out of my own pocket. If you'd like to contribute,{' '}
                            <a href="https://www.patreon.com/user?u=4796225" target="_blank" rel="noreferrer">
                                please join the Patreon
                            </a>
                            .
                        </p>
                        <p>
                            Is the site 100% accurate and infallible? Emphatic{' '}
                            <span className="faqHeroEmphasis">NO</span>. Am I a world-leading expert in cEDH design
                            philosophy? Again, emphatic <span className="faqHeroEmphasis">NO</span>.
                        </p>
                        <p>
                            I'm just a guy that loves to code and loves the game. It is sincerely funny to me how much
                            hate the site gets in Reddit and Discord — it's one thing to celebrate salty deck building,
                            but to <i>literally make people salty just by existing?</i> Amazing! Every stax player's
                            dream.
                        </p>
                        <p>
                            Power levels only really work if your playgroup has a shared source of truth. If this site's
                            flavor isn't for you, a few great alternatives:
                        </p>
                        <div className="faqHeroAlts">
                            <a
                                className="faqHeroAltChip"
                                href="https://edhpowerlevel.com/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                EDHPowerLevel
                            </a>
                            <a className="faqHeroAltChip" href="https://deckcheck.co/" target="_blank" rel="noreferrer">
                                DeckCheck.co
                            </a>
                            <a
                                className="faqHeroAltChip"
                                href="https://mtg.cardsrealm.com/en-us/tools/commander-power-level-calculator"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Cards Realm
                            </a>
                        </div>
                        <p>
                            That said… thanks for visiting :D Hopefully any questions you have are answered below. If
                            not, reach out via the contact form or hop in the Discord.
                        </p>
                    </div>
                </section>

                <NitroAd id="faq" />

                <div className="faqBody">
                    <nav className="faqSideNav" aria-label="FAQ sections">
                        <span className="faqSideNavLabel">Jump to</span>
                        <a className="faqSideNavLink" href="#faq-general">
                            <span className="faqSideNavLinkNumber">01</span>
                            General
                        </a>
                        <a className="faqSideNavLink" href="#faq-common">
                            <span className="faqSideNavLinkNumber">02</span>
                            Common Qs
                        </a>
                        <a className="faqSideNavLink" href="#faq-technical">
                            <span className="faqSideNavLinkNumber">03</span>
                            Technical
                        </a>
                        <a className="faqSideNavLink" href="#faq-complaints">
                            <span className="faqSideNavLinkNumber">04</span>
                            Complaints
                        </a>
                    </nav>

                    <main className="faqContent">
                        <section className="faqSection" id="faq-general">
                            <div className="faqSectionHeader">
                                <span className="faqSectionHeaderNumber">01</span>
                                <h2 className="faqSectionHeaderTitle">General</h2>
                            </div>
                            <FaqPane isOpen={true} title="Who built this?">
                                <p>I've been playing MtG off and on since Revised/3rd Edition came out.</p>
                                <p>
                                    I played a lot of Legacy and Modern competitively, but between work and being a dad,
                                    my available time to keep up on meta shifts and the like has gone down considerably.
                                    I play commander pretty exclusively nowadays for the social interaction.
                                </p>
                                <p>
                                    My decklists are{' '}
                                    <a
                                        href="https://www.moxfield.com/users/grumbledore"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        available on Moxfield
                                    </a>
                                    , and{' '}
                                    <a
                                        href="https://commandersalt.com/user/id/grumbledore"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        here
                                    </a>
                                    .
                                </p>
                                <p>
                                    My play style is primarily stax and control, which honestly probably leads to some
                                    sampling bias in the calculations, but I do strive to be objective 😂.
                                </p>
                                <p>As for my day job, I am a senior software engineer at a large tech firm.</p>
                            </FaqPane>
                            <FaqPane isOpen={true} title="How did this start?">
                                <p>
                                    The idea for this site started because my core playgroup enjoyed making our decks as
                                    salty as we could. Naturally, this turned into a competition.
                                </p>
                                <p>
                                    As the competition evolved, the question of how to fully quantify a deck's salt
                                    level became more and more of a question. We were, of course, intimately familiar
                                    with edhrec.com's famous salt list. In fact, a couple of us used it as a deck
                                    building tool. Given that, the solution was fairly obvious. I made a node script
                                    that aggregated a deck's overall salt score based on the scores of it's constituent
                                    cards.
                                </p>
                                <p>
                                    We showed this to a few other people that we played with in our lgs, and quickly
                                    realized that the idea was amusing enough that other people wanted to participate.
                                </p>
                                <p>
                                    Thus… <i>this</i>.
                                </p>
                                <p>
                                    Once this was publicly available, I noticed that people were thinking that the site
                                    was indicating that saltier decks are inherently better than others. This is largely
                                    my fault, as the site more or less celebrates degeneracy.
                                </p>
                                <p>
                                    However, this did lead me to start looking at how to objectively quantify power
                                    levels. I had seen them discussed a lot, and to be frank, I always kind of thought
                                    it was conceptually dumb. It is far too subjective of a scale to be used outside of
                                    a core playgroup. Still though, it was an interesting problem from a data point of
                                    view, so I started building out a system to try and calculate it.
                                </p>
                            </FaqPane>
                        </section>

                        <section className="faqSection" id="faq-common">
                            <div className="faqSectionHeader">
                                <span className="faqSectionHeaderNumber">02</span>
                                <h2 className="faqSectionHeaderTitle">Common Questions / Misconceptions</h2>
                            </div>
                            <FaqPane isOpen={true} title="Card scores are determined in a vacuum">
                                <p>
                                    Each card is parsed and assigned a score based on mechanical quality of
                                    functionality, as well as casting cost.
                                </p>
                                <p>
                                    However, when determining power levels, the <b>score value of each card</b> is
                                    weighted relative to its synergy with the rest of the deck.
                                </p>
                            </FaqPane>
                            <FaqPane isOpen={true} title="The site prefers infinite combos and cEDH staples">
                                <p>
                                    This one is kind of funny to me. In order to have any concept of an actual power
                                    level scale, you have to have a ceiling.
                                </p>
                                <p>
                                    There is no better representation of maximum efficiency and speed than cEDH meta. So
                                    yes, that is the standard each deck is measured against.
                                </p>
                                <p>
                                    Let's be real here — nobody wants to be told their deck is a 4… especially when it's
                                    actually higher and the site undervalued it. However, I will{' '}
                                    <b>always prefer to undervalue rather than overvalue</b>, simply because I do not
                                    want to be the reason somebody adds a Mana Crypt to a precon and thinks it will
                                    perform well in a cEDH environment/tournament.
                                </p>
                                <p>
                                    Is it objectively perfect and accurate? Absolutely not. I don't even know if it's
                                    possible, to be completely honest. That is, however, the fun part of this.
                                </p>
                            </FaqPane>
                            <FaqPane
                                isOpen={true}
                                title="The site looks at cEDH meta lists to determine card scoring and priorities"
                            >
                                <p>
                                    I index cEDH meta so that I get a list of staples and also to determine what tiers
                                    commanders fall into.
                                </p>
                                <p>
                                    There is{' '}
                                    <b>no logic anywhere in the app that increases deck score based on cEDH staples</b>.
                                    The main reason I get a list of staples is so that I can count them as a category
                                    for salt.
                                </p>
                                <p>
                                    There is <b>no logic that compares deck lists against known cEDH lists</b>, either
                                    from sites like the cEDH database, or internally to decks that have already been
                                    imported to Commandersalt. That would create a lot of confirmation bias and lower
                                    objectivity.
                                </p>
                            </FaqPane>
                        </section>

                        <section className="faqSection" id="faq-technical">
                            <div className="faqSectionHeader">
                                <span className="faqSectionHeaderNumber">03</span>
                                <h2 className="faqSectionHeaderTitle">Technical</h2>
                            </div>
                            <FaqPane isOpen={true} title="What is the tech stack?">
                                <p>The front end is built in React.</p>
                                <p>The backend is built in NodeJS on AWS Lambda services.</p>
                            </FaqPane>
                            <FaqPane isOpen={true} title="How are cards processed?">
                                <p>
                                    This is probably one of the most common questions I get about all of this,
                                    especially when I say there's no AI.
                                </p>
                                <p>The TLDR; is regex. Lots and lots of regex lol.</p>
                                <p>
                                    The more involved answer is that I have tried to build out a system which
                                    effectively "reads" a card in order to determine what it is attempting to do. I took
                                    this path because there are a few main tenants that I am trying to adhere to:
                                </p>
                                <ul>
                                    <li>No crowd sourced data, such as the Scryfall tagger</li>
                                    <li>
                                        No card specific logic: there isn't code that says "Assassin's Trophy is
                                        removal"
                                    </li>
                                    <li>
                                        Automatic card lookups and parsing. This allows the site to utilize cards as
                                        soon as they come out without me having to manually add them to the system
                                        <ul>
                                            <li>
                                                There is a caveat here, in that <b>new mechanics</b> occasionally
                                                require updates to be handled. However, a lot of them boil down to using
                                                the same set of core mechanics that the site already knows how to
                                                handle.
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <p>
                                    Each card has a "contextual map" that is procedurally built for it. This contains
                                    inferences about what players each line of text affects, what types of cards it
                                    cares about, what zones the card is coming from and going to, etc. This is obviously
                                    pretty tricky, and therefore error prone.
                                </p>
                                <p>
                                    Card categories are then assigned based on the contextual data generated in the
                                    previous step.
                                </p>
                                <p>
                                    Example JSON blobs for cards:{' '}
                                    <a
                                        href={`${getDomainPrefix()}/details?id=kenrith_the_returned_king&isCard=true`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Kenrith, the Returned King
                                    </a>
                                    {' · '}
                                    <a
                                        href={`${getDomainPrefix()}/details?id=sheoldred_the_apocalypse&isCard=true`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Sheoldred, the Apocalypse
                                    </a>
                                </p>
                            </FaqPane>
                            <FaqPane isOpen={true} title="How is deck synergy derived?">
                                <p>Synergy is hands down the most complicated and error prone part of the site.</p>
                                <p>
                                    In a nut shell, it procedurally analyzes each line of text on each card against each
                                    line of text on each other card in the deck. It then builds a map of causal
                                    relationships between these effects.
                                </p>
                            </FaqPane>
                        </section>

                        <section className="faqSection" id="faq-complaints">
                            <div className="faqSectionHeader">
                                <span className="faqSectionHeaderNumber">04</span>
                                <h2 className="faqSectionHeaderTitle">I Have Complaints!</h2>
                            </div>
                            <div className="faqComplaintsCard">
                                <p>You have the luxury of embarking upon one of several paths at this point:</p>
                                <ul className="faqComplaintsList">
                                    <li>
                                        <b>Contribute —</b> Let me know! Either by emailing me via the contact form, or
                                        by joining the Discord.
                                    </li>
                                    <li>
                                        <b>Disparage —</b> Grab your pitchforks and angrily head on over to your
                                        favorite subreddit or Discord of choice. This is, of course, the most
                                        emotionally satisfying option.
                                    </li>
                                    <li>
                                        <b>Move on —</b> Nobody is actually forcing you to use this… are they? (If they
                                        are, I am genuinely curious lol.)
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </Flex>
    );
}
